/**
 * AppShell — the new (post-Cloudscape, ADR-001) application frame.
 *
 * Three regions:
 *   - Top bar       — brand + breadcrumb + user menu (Radix DropdownMenu)
 *   - Side nav      — fixed-width left rail; collapsible (persists per
 *                     employee). Collapsed state renders a 56px icon-only
 *                     rail (NOT a 0px hidden nav) so users can still see and
 *                     click items. Hover tooltips surface the label.
 *   - Content slot  — `<Outlet />` rendered through `react-router-dom`.
 *                     A fixed 320px right-panel column is rendered on
 *                     opt-in routes (currently `/play`).
 *
 * Post-Cloudscape rebuild (ADR-001): every protected route renders through
 * this shell. The Cloudscape-era `WayneAppLayout` and its dependent
 * organisms have been removed.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as Tooltip from '@radix-ui/react-tooltip';
import {
  ChevronDown,
  HomeIcon,
  PlayIcon,
  ListChecks,
  Plug,
  Brain,
  Workflow,
  Settings as SettingsIcon,
  PanelLeft,
  Sun,
  Moon,
  Hash,
  MessageCircle,
} from 'lucide-react';
import { z } from 'zod';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { tourStorageKey } from '@/components/tour';
import { FeedbackPanel, useFeedbackPanel } from '@/components/organisms/FeedbackPanel';
import { CommandPalette } from '@/components/organisms/CommandPalette';
import { useCommandPalette } from '@/hooks/useCommandPalette';
import { NotificationBell } from '@/components/organisms/NotificationCenter';
import RightPanel from './RightPanel';

const HOME_TOUR_ID = 'home';
const TOUR_START_STORAGE_KEY = 'wayne.tour.start';

/**
 * Replay the Home tour: clear the persisted "completed" status, seed the
 * tour-start sessionStorage flag Home reads on mount, and navigate to /.
 *
 * `useTour` only auto-fires when the persisted status is `unstarted`; we
 * have to remove the localStorage record before Home re-renders or the
 * `start()` call there would be a no-op.
 */
function replayHomeTour(
  employeeId: string | undefined,
  navigate: (path: string) => void,
): void {
  try {
    window.localStorage.removeItem(tourStorageKey(HOME_TOUR_ID, employeeId ?? null));
  } catch {
    /* ignore — storage may be unavailable in some browser modes */
  }
  try {
    window.sessionStorage.setItem(TOUR_START_STORAGE_KEY, 'home');
  } catch {
    /* ignore */
  }
  navigate('/');
}

const PERSIST_PREFIX = 'wayne.v2.shell';

const PersistedSchema = z.object({
  version: z.literal(1),
  navOpen: z.boolean(),
});
type Persisted = z.infer<typeof PersistedSchema>;

interface NavItem {
  href: string;
  label: string;
  icon: ReactNode;
  /** Stable key for `data-tour="rail-<key>"` anchors used by the Home tour. */
  tourKey: string;
}

const NAV_ITEMS: readonly NavItem[] = [
  { href: '/', label: 'Home', icon: <HomeIcon size={15} />, tourKey: 'home' },
  { href: '/play', label: 'Play', icon: <PlayIcon size={15} />, tourKey: 'play' },
  { href: '/tasks', label: 'Tasks', icon: <ListChecks size={15} />, tourKey: 'tasks' },
  { href: '/connections', label: 'Connections', icon: <Plug size={15} />, tourKey: 'connections' },
  { href: '/memory', label: 'Memory', icon: <Brain size={15} />, tourKey: 'memory' },
  { href: '/workflows', label: 'Workflows', icon: <Workflow size={15} />, tourKey: 'workflows' },
  { href: '/settings', label: 'Settings', icon: <SettingsIcon size={15} />, tourKey: 'settings' },
];

/** Routes that opt into the fixed right-panel column. */
const RIGHT_PANEL_ROUTES: readonly string[] = ['/play'];

function storageKey(employeeId: string): string {
  return `${PERSIST_PREFIX}.${employeeId}`;
}

function readPersisted(employeeId: string): Persisted | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(storageKey(employeeId));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    const result = PersistedSchema.safeParse(parsed);
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}

function writePersisted(employeeId: string, value: Persisted): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey(employeeId), JSON.stringify(value));
  } catch {
    /* ignore */
  }
}

function pageTitle(pathname: string): string {
  if (pathname === '/' || pathname === '/home') return 'Home';
  const item = NAV_ITEMS.find((it) =>
    it.href === '/' ? false : pathname.startsWith(it.href),
  );
  return item?.label ?? 'Wayne';
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0]?.[0] ?? '?').toUpperCase();
  return (
    (parts[0]?.[0] ?? '') + (parts[parts.length - 1]?.[0] ?? '')
  ).toUpperCase();
}

function shouldShowRightPanel(pathname: string): boolean {
  return RIGHT_PANEL_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`),
  );
}

export default function AppShell(): JSX.Element {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(true);
  const [hoveredNav, setHoveredNav] = useState<string | null>(null);
  const feedbackPanel = useFeedbackPanel();
  const commandPalette = useCommandPalette();

  useEffect(() => {
    if (!user) return;
    const stored = readPersisted(user.employeeId);
    if (stored) setNavOpen(stored.navOpen);
  }, [user]);

  const toggleNav = () => {
    const next = !navOpen;
    setNavOpen(next);
    if (user) writePersisted(user.employeeId, { version: 1, navOpen: next });
  };

  const isActive = (href: string): boolean => {
    if (href === '/') return location.pathname === '/' || location.pathname === '/home';
    return location.pathname === href || location.pathname.startsWith(`${href}/`);
  };

  const navWidth = navOpen ? '240px' : '56px';
  const showRightPanel = shouldShowRightPanel(location.pathname);
  const gridTemplateColumns = showRightPanel
    ? `${navWidth} 1fr 320px`
    : `${navWidth} 1fr`;

  return (
    <Tooltip.Provider delayDuration={300}>
      <div className="flex flex-col min-h-screen bg-wayne-bg text-wayne-ink">
        {/* Top bar */}
        <header
          className="flex items-center gap-4 h-14 px-7 border-b border-wayne-hairline bg-wayne-bg shrink-0"
          style={{ transition: 'background-color 400ms var(--ease-default), border-color 300ms var(--ease-default), color 300ms var(--ease-default)' }}
        >
          <button
            type="button"
            aria-label={navOpen ? 'Collapse navigation' : 'Expand navigation'}
            onClick={toggleNav}
            className="p-1.5 rounded-md text-wayne-ink-3 hover:text-wayne-ink hover:bg-wayne-surface-2 transition-colors -ml-1.5"
          >
            <PanelLeft size={16} />
          </button>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="font-serif text-[19px] font-semibold tracking-tight leading-none bg-transparent border-0 p-0 cursor-pointer text-wayne-ink"
          >
            Wayne <span className="text-wayne-accent">AI</span>
          </button>
          <span className="text-wayne-ink-3 text-[13px]">›</span>
          <span className="text-wayne-ink-2 text-[13px] font-medium">
            {pageTitle(location.pathname)}
          </span>
          <div className="flex-1" />

          {/* Cmd+K command palette hint */}
          <button
            type="button"
            data-tour="shell-cmdk"
            aria-label="Open command palette"
            onClick={commandPalette.toggle}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-wayne-hairline text-wayne-ink-3 text-[12px] hover:border-wayne-hairline-2 hover:text-wayne-ink-2 transition-colors"
          >
            <span className="text-[11px]" aria-hidden="true">⌘K</span>
          </button>

          {/* Theme toggle */}
          <button
            type="button"
            data-tour="shell-theme"
            onClick={toggleTheme}
            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            className="w-8 h-8 rounded-full bg-wayne-surface border border-wayne-hairline flex items-center justify-center hover:border-wayne-accent hover:scale-[1.08] active:scale-95 transition-all"
            style={{ transition: 'border-color 200ms var(--ease-default), transform 280ms var(--ease-bounce)' }}
          >
            <span
              key={theme}
              className="animate-[themeIconIn_300ms_var(--ease-bounce)_both]"
            >
              {theme === 'dark' ? <Sun size={16} className="text-wayne-ink-2" /> : <Moon size={16} className="text-wayne-ink-2" />}
            </span>
          </button>

          {/* Slack status indicator */}
          <a
            href="/settings"
            data-tour="shell-slack"
            className="relative w-8 h-8 rounded-full bg-wayne-surface border border-wayne-hairline flex items-center justify-center hover:border-wayne-accent transition-all"
            title="Slack: Connected"
            onClick={(e) => { e.preventDefault(); navigate('/settings'); }}
          >
            <Hash size={14} className="text-wayne-ink-2" />
            <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-wayne-green border-2 border-wayne-bg" />
          </a>

          {/* Notification bell */}
          <NotificationBell />

          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button
                type="button"
                data-tour="shell-account"
                className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-full border border-transparent hover:border-wayne-hairline transition-colors"
              >
                <span className="w-7 h-7 rounded-full bg-wayne-accent text-white inline-flex items-center justify-center text-[12px] font-semibold">
                  {user ? initials(user.displayName) : '?'}
                </span>
                <span className="text-[13px]">
                  {user?.displayName ?? 'Account'}
                </span>
                <ChevronDown size={12} className="text-wayne-ink-3" />
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                align="end"
                sideOffset={6}
                className="min-w-[180px] rounded-lg border border-wayne-hairline bg-wayne-surface shadow-lg p-1 text-[13px]"
              >
                <DropdownMenu.Item
                  onSelect={() => navigate('/settings')}
                  className="px-3 py-2 rounded-md cursor-pointer outline-none data-[highlighted]:bg-wayne-surface-2"
                >
                  Settings
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  data-testid="replay-tour"
                  onSelect={() => replayHomeTour(user?.employeeId, navigate)}
                  className="px-3 py-2 rounded-md cursor-pointer outline-none data-[highlighted]:bg-wayne-surface-2"
                >
                  Replay tour
                </DropdownMenu.Item>
                <DropdownMenu.Separator className="h-px bg-wayne-hairline my-1" />
                <DropdownMenu.Item
                  onSelect={() => {
                    void logout();
                  }}
                  className="px-3 py-2 rounded-md cursor-pointer outline-none data-[highlighted]:bg-wayne-surface-2"
                >
                  Sign out
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </header>

        <div
          className="flex-1 grid"
          style={{
            gridTemplateColumns,
            transition: 'grid-template-columns 200ms ease',
          }}
        >
          {/* Side nav */}
          <nav
            data-tour="shell-nav"
            className={[
              'border-r border-wayne-hairline overflow-hidden',
              navOpen ? 'px-4 py-7' : 'px-2 py-7',
            ].join(' ')}
            style={{ transition: 'background-color 400ms var(--ease-default), border-color 300ms var(--ease-default), color 300ms var(--ease-default)' }}
            aria-label="Primary"
          >
            {navOpen ? (
              <div className="text-wayne-ink-3 uppercase tracking-[0.08em] text-[10px] px-3 pb-2 font-medium">
                Platform
              </div>
            ) : null}
            <ul className="flex flex-col gap-0.5">
              {NAV_ITEMS.map((item) => {
                const active = isActive(item.href);
                const isHovered = hoveredNav === item.href;
                const buttonTransition = isHovered
                  ? 'background-color 120ms var(--ease-default), color 120ms var(--ease-default), transform 80ms linear'
                  : 'background-color 200ms var(--ease-default), color 200ms var(--ease-default), transform 80ms linear';
                const buttonClasses = [
                  'relative z-10 w-full flex items-center rounded-lg text-[14px] text-left active:scale-[0.97]',
                  navOpen
                    ? 'gap-3 px-3 py-2'
                    : 'gap-0 px-2 py-2 justify-center',
                  active
                    ? 'text-wayne-ink border border-transparent'
                    : 'text-wayne-ink-2 hover:text-wayne-ink hover:bg-wayne-surface-2 border border-transparent',
                ].join(' ');

                const button = (
                  <button
                    type="button"
                    onClick={() => navigate(item.href)}
                    aria-label={item.label}
                    className={buttonClasses}
                    style={{ transition: buttonTransition }}
                    onPointerEnter={() => setHoveredNav(item.href)}
                    onPointerLeave={() => setHoveredNav(null)}
                  >
                    <span
                      className={
                        active ? 'text-wayne-ink' : 'text-wayne-ink-3'
                      }
                    >
                      {item.icon}
                    </span>
                    {navOpen ? <span>{item.label}</span> : null}
                  </button>
                );

                return (
                  <li key={item.href} data-tour={`rail-${item.tourKey}`} className="relative">
                    <span
                      className="absolute inset-0 rounded-lg bg-wayne-surface border border-wayne-hairline"
                      style={{
                        transform: active ? 'scale(1)' : 'scale(0.95)',
                        opacity: active ? 1 : 0,
                        transition: 'transform 200ms var(--ease-spring), opacity 200ms var(--ease-spring)',
                        zIndex: 0,
                      }}
                    />
                    {navOpen ? (
                      button
                    ) : (
                      <Tooltip.Root>
                        <Tooltip.Trigger asChild>{button}</Tooltip.Trigger>
                        <Tooltip.Portal>
                          <Tooltip.Content
                            side="right"
                            sideOffset={6}
                            className="bg-wayne-ink text-wayne-bg text-[12px] px-2 py-1 rounded shadow-md"
                          >
                            {item.label}
                          </Tooltip.Content>
                        </Tooltip.Portal>
                      </Tooltip.Root>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Main content */}
          <main className="min-w-0 overflow-auto">
            <Outlet />
          </main>

          {/* Right panel — opt-in per route. Only renders for routes in
              RIGHT_PANEL_ROUTES. Future routes can join the list above. */}
          {showRightPanel ? (
            <aside
              aria-label="Project context"
              className="border-l border-wayne-hairline p-6 pb-20 overflow-auto"
              style={{ transition: 'background-color 400ms var(--ease-default), border-color 300ms var(--ease-default), color 300ms var(--ease-default)' }}
            >
              <RightPanel />
            </aside>
          ) : null}
        </div>
      </div>

      {/* Feedback FAB — shifts left when right panel is visible to avoid overlap */}
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <button
            type="button"
            data-tour="shell-feedback"
            aria-label="Send feedback"
            className="fixed bottom-6 w-9 h-9 rounded-full bg-wayne-surface border border-wayne-hairline text-wayne-ink-3 flex items-center justify-center shadow-sm z-[9000] hover:border-wayne-accent hover:text-wayne-accent active:scale-95"
            style={{
              right: showRightPanel ? '344px' : '24px',
              transition: 'border-color 200ms var(--ease-default), color 200ms var(--ease-default), transform 280ms var(--ease-bounce), right 200ms var(--ease-default)',
            }}
            onClick={feedbackPanel.toggle}
          >
            <MessageCircle size={16} />
          </button>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            side="top"
            sideOffset={6}
            className="bg-wayne-ink text-wayne-bg text-[12px] px-2 py-1 rounded shadow-md"
          >
            Send feedback
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>

      <FeedbackPanel open={feedbackPanel.open} onClose={feedbackPanel.close} rightOffset={showRightPanel ? 344 : 24} />
      <CommandPalette open={commandPalette.open} onOpenChange={commandPalette.setOpen} />
    </Tooltip.Provider>
  );
}
